import { FC, useEffect, useMemo, useState } from 'react';

import { TeamMembersStats } from '../../../../../../../__generated__/GQL';
import categoriesLocales, {
  LocalizedCategories,
} from '../../../../../../../common/categoriesLocales';
import Heading from '../../../../../../../components/_core/Heading';
import {
  DASHBOARD_FILTER_TO_STAT_MODE,
  paymentMethodItems,
  TRANSACTION_AGGREGATION_EMPTY_STATES,
} from '../../../../../common';
import dashboardLocales from '../../../../../common/locales';
import { DashboardFilter, FiltersData } from '../../../../../types';
import { FilterGroup } from '../../../../components/FilterGroup';
import LoadingFilterGroup from '../../../../components/LoadingFilterGroup';
import {
  TransactionAggregationItemWithId,
  TransactionAggregationTable,
} from '../../../../components/TransactionAggregationTable/table';
import { GraphType } from '../../GraphSelector';
import locales from '../locales';

interface DashboardFiltersProps {
  graphType: GraphType;
  data: FiltersData;
}

const DashboardFilters: FC<DashboardFiltersProps> = ({ data, graphType }) => {
  const { merchants, paymentMethod, teamMembers } = data;
  const [activeAggregationFilter, setActiveAggregationFilter] =
    useState<DashboardFilter>(DashboardFilter.operationType);

  useEffect(() => {
    if (graphType === 'payin') {
      setActiveAggregationFilter(DashboardFilter.operationType);
    } else if (graphType === 'payout') {
      setActiveAggregationFilter(DashboardFilter.merchant);
    }
  }, [graphType]);

  const aggregationItems = useMemo<TransactionAggregationItemWithId[]>(() => {
    if (activeAggregationFilter === DashboardFilter.operationType) {
      return paymentMethodItems(graphType, paymentMethod);
    }

    if (activeAggregationFilter === DashboardFilter.merchant) {
      return (merchants?.map((merchant) => {
        const {
          merchantCategory,
          merchantName,
          merchantPayoutValue,
          merchantTransactionsCount,
        } = merchant;
        return {
          currentValue: merchantPayoutValue,
          id: merchantName + merchantCategory,
          subtitle:
            merchantCategory in categoriesLocales
              ? categoriesLocales[merchantCategory as LocalizedCategories]
              : merchantCategory,
          title: merchantName,
          transactionCount: merchantTransactionsCount,
        };
      }) || []) as TransactionAggregationItemWithId[];
    }

    if (activeAggregationFilter === DashboardFilter.team) {
      const previousTeamMemberValues = teamMembers?.oldValue?.reduce(
        (agg, teamMember) => {
          agg[teamMember.teamMemberCompanyUserId] = teamMember;
          return agg;
        },
        {} as Record<string, TeamMembersStats | undefined>,
      );

      return (teamMembers?.newValue?.map((teamMember) => {
        const {
          teamMemberCompanyUserId,
          teamMemberName,
          teamMemberPayoutValue,
          teamMemberTransactionsCount,
        } = teamMember;

        return {
          currentValue: teamMemberPayoutValue,
          delta:
            teamMemberPayoutValue -
            (previousTeamMemberValues?.[teamMemberCompanyUserId]
              ?.teamMemberPayoutValue ?? 0),
          id: teamMemberCompanyUserId,
          subtitle: dashboardLocales.formatString(
            teamMemberTransactionsCount === 1
              ? dashboardLocales.transactionCount.singular
              : dashboardLocales.transactionCount.plural,
            {
              count: teamMemberTransactionsCount,
            },
          ),
          title: teamMemberName,
        };
      }) || []) as TransactionAggregationItemWithId[];
    }

    return [];
  }, [
    activeAggregationFilter,
    paymentMethod,
    merchants,
    teamMembers,
    graphType,
  ]);

  if (graphType === 'balance') {
    return null;
  }

  if (!paymentMethod) {
    return <LoadingFilterGroup />;
  }

  return (
    <>
      <Heading
        fontFamily="Mabry Pro"
        marginBottom="space-16"
        size="sm"
        variant="primary"
      >
        {locales.details}
      </Heading>

      {graphType === 'payout' ? (
        <FilterGroup
          activeFilterId={activeAggregationFilter}
          filters={[
            {
              icon: 'company',
              id: DashboardFilter.merchant,
              label: locales.detailFilters.transactions,
            },
            {
              icon: 'profile',
              id: DashboardFilter.team,
              label: locales.detailFilters.team,
            },
            {
              icon: 'tag',
              id: DashboardFilter.operationType,
              label: locales.detailFilters.operationType,
            },
          ]}
          marginBottom="space-24"
          onFilterClicked={setActiveAggregationFilter}
        />
      ) : null}

      <TransactionAggregationTable
        colorSchema={graphType === 'payout' ? 'arrow-reversed' : 'arrow-normal'}
        emptyStateComponent={
          TRANSACTION_AGGREGATION_EMPTY_STATES[activeAggregationFilter] ||
          TRANSACTION_AGGREGATION_EMPTY_STATES[DashboardFilter.operationType]
        }
        items={aggregationItems}
        mode={DASHBOARD_FILTER_TO_STAT_MODE[activeAggregationFilter] || 'delta'}
      />
    </>
  );
};

export default DashboardFilters;
